import { ApplicationConfig, ErrorHandler, LOCALE_ID, APP_INITIALIZER } from '@angular/core';
import { provideRouter } from '@angular/router';

import { routes } from './app.routes';
import { provideClientHydration, withEventReplay } from '@angular/platform-browser';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideNgxMask } from 'ngx-mask';
import { authInterceptor } from './shared/interceptors/auth.interceptor';
import { errorInterceptor } from './shared/interceptors/error.interceptor';
import { apiEnvironmentInterceptor } from './shared/interceptors/api-environment.interceptor';
import { provideTranslateService, TranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { SlackErrorHandler } from './shared/services/slack-error-handler';
import { provideServiceWorker } from '@angular/service-worker';
import { environment } from '../environments/environment';

/**
 * Está rodando sob `ng serve`?
 *
 * O dev server **não emite** `ngsw-worker.js`, então registrar ali daria 404 a cada carga. O
 * recorte é pela porta, e não por "é localhost": um build servido localmente (`npx serve -s
 * dist/template/browser`) precisa registrar normalmente, senão não há como testar o PWA fora de
 * um ambiente publicado.
 */
function isAngularDevServer(): boolean {
  if (typeof location === 'undefined') return false;
  const isLocal = ['localhost', '127.0.0.1', '[::1]'].includes(location.hostname);
  return isLocal && location.port === '4200';
}

// Função para inicializar as traduções antes da aplicação iniciar
// Seguindo as melhores práticas: usar o TranslateHttpLoader nativo e apenas configurar o idioma padrão
export function appInitializerFactory(translate: TranslateService) {
  return () => {
    // Configurar idiomas disponíveis
    translate.addLangs(['pt', 'en', 'es']);
    translate.setDefaultLang('pt');
    
    // Usar o idioma padrão - o TranslateHttpLoader carregará automaticamente as traduções
    return translate.use('pt').toPromise();
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes),
    BsDatepickerModule.forRoot().providers!,
    provideAnimations(),
    provideHttpClient(
      // Ordem importa: o `errorInterceptor` fica primeiro para ver o resultado final (após o retry de
      // refresh do `authInterceptor`); o `apiEnvironmentInterceptor` reescreve a base URL antes
      // do `authInterceptor` anexar o Bearer, garantindo que o token seja
      // aplicado à URL definitiva.
      withInterceptors([errorInterceptor, apiEnvironmentInterceptor, authInterceptor])
    ),
    provideNgxMask(),
    // O loader precisa ir DENTRO da config: o `provideTranslateService` registra por padrão o
    // `TranslateNoOpLoader`, e um `provideTranslateHttpLoader` registrado à parte é descartado
    // (o último provider vence). Sem isso nenhuma tradução é baixada pelo APP_INITIALIZER.
    provideTranslateService({
      fallbackLang: 'pt',
      loader: provideTranslateHttpLoader({
        prefix: '/assets/i18n/',
        suffix: '.json'
      })
    }),
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializerFactory,
      deps: [TranslateService],
      multi: true
    },
    { provide: LOCALE_ID, useValue: 'pt-BR' },
    { provide: ErrorHandler, useClass: SlackErrorHandler },
    // PWA. `registerWhenStable` espera a aplicação ficar ociosa antes de registrar, para o
    // download do service worker não competir com as primeiras requisições da tela.
    //
    // O guard do dev server é necessário além da flag de ambiente: `ng serve` usa a configuração
    // `development` (que tem a flag ligada, porque o ambiente de dev publicado precisa do PWA),
    // mas o dev server **não emite** `ngsw-worker.js` — sem o guard, cada carga local tentaria
    // registrar um arquivo inexistente e sujaria o console com 404.
    //
    // Registra o `app-sw.js` (em `public/`), que embrulha o `ngsw-worker.js` e deixa as chamadas
    // à API (outra origem) fora do worker: o Angular converte qualquer falha delas em um 504
    // sintético. `updateViaCache: 'none'` faz o navegador revalidar o worker e o script importado
    // a cada checagem de atualização, sem depender do cache HTTP.
    provideServiceWorker('app-sw.js', {
      enabled: environment.serviceWorker && !isAngularDevServer(),
      registrationStrategy: 'registerWhenStable:30000',
      updateViaCache: 'none'
    })
  ],

};
